/*
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved.
* Distributed under the TruVideo proprietary license.
*/
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, AlertTriangle, Bot, BookOpen, MessageSquare, MessagesSquare, FileCode, Settings2, Server, Filter, Pencil
} from 'lucide-react';
import { aiAgentService } from '../services/aiAgentService';
import { AI_AGENT_ERROR_CODES, getApiErrorMessage } from '../services/knowledgeBaseService';
import StatusBadge from '../components/StatusBadge';
import AgentChatPreview from '../components/aiAgents/AgentChatPreview';
import PromptVersionsPanel from '../components/aiAgents/PromptVersionsPanel';
import toastService from '../utils/toastService';
import { AI_AGENT_TYPE_MAIN, getAiAgentProviderLabel, getAiAgentTypeLabel, isMetadataClassifierType } from '../constants/aiAgents';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionContext';
import AgenticAiUnavailable from '../components/aiAgents/AgenticAiUnavailable';
import { getAgenticAiServiceError } from '../utils/agenticAiErrors';
import { formatDateTime } from '../utils/agenticAiDates';
import { getActivePromptVersion } from '../utils/promptVersions';
import { ACCOUNT_LABEL } from '../constants/entityLabels';

const LIST_ROUTE = '/ai-agents';

const AiAgentDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: agentId } = useParams();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { can } = usePermissions();
  const canUpdateAgent = can.ai_agents.update();
  const canViewKnowledgeBases = can.knowledge_bases.view();

  // Platform-wide view carries the account in the URL; account / sub-account users use their own.
  const showAccountField = !user?.accountUID;
  const accountId = showAccountField ? (searchParams.get('accountId') || '') : user?.accountUID;
  const accountQuery = showAccountField && accountId ? `?accountId=${encodeURIComponent(accountId)}` : '';
  const accountParams = showAccountField ? { accountId } : {};

  const [agent, setAgent] = useState(null);
  // Label of the current system prompt version; null when unknown (the badge is then hidden).
  const [promptVersion, setPromptVersion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [integrationError, setIntegrationError] = useState(null);
  // Set when the Agentic AI platform itself does not respond properly.
  const [serviceError, setServiceError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [previewOpen, setPreviewOpen] = useState(false);

  const backToList = () => navigate(`${LIST_ROUTE}${accountQuery}`);

  useEffect(() => {
    if (user && showAccountField && !accountId) {
      navigate(LIST_ROUTE, { replace: true });
    }
  }, [user, showAccountField, accountId, navigate]);

  useEffect(() => {
    if (!accountId) { return undefined; }
    let cancelled = false;
    const fetchAgent = async () => {
      try {
        setLoading(true);
        setNotFound(false);
        setIntegrationError(null);
        setServiceError(null);
        setPromptVersion(null);
        const params = showAccountField ? { accountId } : {};
        const [{ data }, versionsRes] = await Promise.all([
          aiAgentService.getAiAgent(agentId, params),
          // Only for the version badge: a failure leaves the badge out, never the page.
          aiAgentService.getPromptVersions(agentId, params).catch(() => null),
        ]);
        if (cancelled) { return; }
        setAgent(data?.data || null);
        const versions = Array.isArray(versionsRes?.data?.data) ? versionsRes.data.data : [];
        setPromptVersion(getActivePromptVersion(versions));
      } catch (err) {
        if (cancelled) { return; }
        const message = getApiErrorMessage(err, 'Failed to load AI agent');
        if (getAgenticAiServiceError(err)) {
          setServiceError(getAgenticAiServiceError(err));
        } else if (AI_AGENT_ERROR_CODES.includes(err.response?.data?.code)) {
          setIntegrationError(message);
        } else if (err.response?.status === 404) {
          setNotFound(true);
        } else {
          toastService.error(message);
        }
      } finally {
        if (!cancelled) { setLoading(false); }
      }
    };
    fetchAgent();
    return () => { cancelled = true; };
  }, [agentId, accountId, showAccountField, reloadKey]);

  const openKnowledgeBase = (kb) => {
    navigate(`/knowledge-bases/${encodeURIComponent(kb.id)}/documents${accountQuery}`, {
      state: { knowledgeBase: { id: kb.id, name: kb.name } },
    });
  };

  // A restored version is already live: show its prompt and label. Merged, so fields the
  // restore response may leave out (e.g. knowledge base names) are kept.
  const handleRestored = (restoredAgent, version) => {
    setAgent((a) => ({ ...a, ...(restoredAgent || {}) }));
    setPromptVersion((version?.version || '').trim() || null);
  };

  const card = 'glass-card rounded-2xl p-5 sm:p-6 shadow-sm border border-base-content/10 space-y-4';
  // `titleBadge` is a pill next to the title (e.g. the prompt version); `badge` is right-aligned text.
  const sectionHeader = (Icon, title, badge = null, titleBadge = null) => (
    <div className="flex items-center justify-between pb-3 border-b border-base-content/10">
      <div className="flex items-center gap-2">
        <Icon size={18} className="text-primary" />
        <h2 className="text-base font-black text-base-content uppercase tracking-wider">{title}</h2>
        {titleBadge && (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold font-mono bg-primary/10 text-primary border border-primary/20">
            {titleBadge}
          </span>
        )}
      </div>
      {badge && <span className="text-[10px] text-base-content/40 font-mono">{badge}</span>}
    </div>
  );
  const field = (label, value, mono = false) => (
    <div className="space-y-1">
      <p className="text-[10px] font-black text-base-content/50 uppercase tracking-widest">{label}</p>
      <p className={`text-sm font-semibold text-base-content break-words ${mono ? 'font-mono' : ''}`}>
        {value ?? '—'}
      </p>
    </div>
  );
  const textBlock = (value, emptyText) => (value ? (
    <p className="p-4 rounded-xl bg-base-200/50 border border-base-content/5 text-sm text-base-content/80 whitespace-pre-wrap break-words">
      {value}
    </p>
  ) : (
    <p className="text-xs text-base-content/40">{emptyText}</p>
  ));

  const renderContent = () => {
    if (loading) {
      return (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-40 bg-base-200/50 rounded-2xl animate-pulse"></div>
          ))}
        </div>
      );
    }
    if (serviceError) {
      return <AgenticAiUnavailable error={serviceError} onRetry={() => setReloadKey((k) => k + 1)} />;
    }
    if (integrationError) {
      return (
        <div role="alert" className="alert alert-warning rounded-xl text-sm font-semibold">
          <AlertTriangle size={18} className="shrink-0" />
          <span>{integrationError}</span>
        </div>
      );
    }
    if (notFound || !agent) {
      return (
        <div className="glass-card rounded-2xl shadow-sm border border-base-content/10 py-16 px-6 flex flex-col items-center justify-center text-center space-y-3">
          <div className="w-14 h-14 rounded-2xl bg-base-200 flex items-center justify-center text-base-content/30 shadow-inner">
            <Bot size={28} />
          </div>
          <div>
            <h3 className="text-base font-black text-base-content">AI agent not found</h3>
            <p className="text-xs text-base-content/40 max-w-sm mt-1">
              It may have been removed, or it does not belong to this {ACCOUNT_LABEL.singularLower}.
            </p>
          </div>
          <button
            type="button"
            onClick={backToList}
            className="btn btn-sm btn-ghost border border-base-content/10 rounded-xl font-bold text-xs"
          >
            Back to AI Agents
          </button>
        </div>
      );
    }

    // A metadata classifier agent has no greeting, knowledge bases or MCP servers.
    const isClassifierAgent = isMetadataClassifierType(agent.type);
    const hasClassifier = Boolean(agent.classifierPrompt) || agent.classifierResponseFormat !== null;
    const classifierFormat = typeof agent.classifierResponseFormat === 'string'
      ? agent.classifierResponseFormat
      : JSON.stringify(agent.classifierResponseFormat, null, 2);

    return (
      <div className="space-y-6">
        {/* Configuration */}
        <div className={card}>
          {sectionHeader(Settings2, 'Configuration')}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {field('Role', agent.role)}
            {field('Type', agent.type ? getAiAgentTypeLabel(agent.type) : null)}
            {field('Provider', agent.provider ? getAiAgentProviderLabel(agent.provider) : null)}
            {field('Model', agent.model, true)}
            {field('Temperature', agent.temperature ?? null, true)}
            {agent.type === AI_AGENT_TYPE_MAIN && (
              <div className="space-y-1">
                <p className="text-[10px] font-black text-base-content/50 uppercase tracking-widest">Metadata Classifier Agent</p>
                {agent.metadataClassifierAgent ? (
                  <button
                    type="button"
                    onClick={() => navigate(`${LIST_ROUTE}/${encodeURIComponent(agent.metadataClassifierAgent.id)}${accountQuery}`)}
                    className="text-sm font-semibold text-primary hover:underline break-words text-left"
                  >
                    {agent.metadataClassifierAgent.name || agent.metadataClassifierAgent.id}
                  </button>
                ) : (
                  <p className="text-sm font-semibold text-base-content">None</p>
                )}
              </div>
            )}
            <div className="space-y-1">
              <p className="text-[10px] font-black text-base-content/50 uppercase tracking-widest">Status</p>
              <StatusBadge status={agent.isLive ? 'live' : 'not_live'} />
            </div>
            {field('Created', formatDateTime(agent.createdAt))}
            {field('Last Updated', formatDateTime(agent.updatedAt))}
          </div>
        </div>

        {/* Greeting message */}
        {!isClassifierAgent && (
          <div className={card}>
            {sectionHeader(MessageSquare, 'Greeting Message')}
            {textBlock(agent.greetingMessage, 'No greeting message configured.')}
          </div>
        )}

        {/* System prompt */}
        <div className={card}>
          {sectionHeader(FileCode, 'Prompt', null, promptVersion)}
          {/* View previous versions; restoring needs update access. */}
          <PromptVersionsPanel
            agentId={agentId}
            accountParams={accountParams}
            showNewVersion={false}
            canRestore={canUpdateAgent}
            onRestored={handleRestored}
          />
          {textBlock(agent.prompt, 'No prompt configured.')}
        </div>

        {/* Knowledge bases */}
        {!isClassifierAgent && (
          <div className={card}>
            {sectionHeader(BookOpen, 'Knowledge Bases', `${agent.knowledgeBases?.length || 0} linked`)}
            {agent.knowledgeBases?.length ? (
              <div className="flex flex-wrap gap-2">
                {agent.knowledgeBases.map((kb) => (kb.name ? (
                  canViewKnowledgeBases ? (
                    <button
                      key={kb.id}
                      type="button"
                      onClick={() => openKnowledgeBase(kb)}
                      className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border bg-primary/10 text-primary border-primary/20 hover:bg-primary/20 transition-colors"
                    >
                      <BookOpen size={12} />
                      <span>{kb.name}</span>
                    </button>
                  ) : (
                    <span
                      key={kb.id}
                      className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border bg-primary/10 text-primary border-primary/20"
                    >
                      <BookOpen size={12} />
                      <span>{kb.name}</span>
                    </span>
                  )
                ) : (
                  <span
                    key={kb.id}
                    className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border bg-base-200 text-base-content/50 border-base-content/5"
                    title={kb.id}
                  >
                    <BookOpen size={12} />
                    <span>Unknown knowledge base</span>
                    <span className="font-mono text-[10px]">{kb.id}</span>
                  </span>
                )))}
              </div>
            ) : (
              <p className="text-xs text-base-content/40">No knowledge bases linked.</p>
            )}
          </div>
        )}

        {/* MCP servers */}
        {!isClassifierAgent && (
          <div className={card}>
            {sectionHeader(Server, 'MCP Servers', `${agent.mcpServers?.length || 0} connected`)}
            {agent.mcpServers?.length ? (
              <div className="flex flex-wrap gap-2">
                {agent.mcpServers.map((server, idx) => (
                  <span
                    // eslint-disable-next-line react/no-array-index-key
                    key={`${server}-${idx}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border bg-base-200 text-base-content/70 border-base-content/5"
                  >
                    <Server size={12} />
                    <span>{server}</span>
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-xs text-base-content/40">No MCP servers connected.</p>
            )}
            {agent.mcpServersConfig?.length > 0 && (
              <div className="space-y-1.5">
                <p className="text-[10px] font-black text-base-content/50 uppercase tracking-widest">Configuration</p>
                <pre className="p-4 rounded-xl bg-base-200/50 border border-base-content/5 text-xs font-mono text-base-content/80 overflow-x-auto whitespace-pre-wrap break-all">
                  {JSON.stringify(agent.mcpServersConfig, null, 2)}
                </pre>
              </div>
            )}
          </div>
        )}

        {/* Classifier (only when configured) */}
        {hasClassifier && (
          <div className={card}>
            {sectionHeader(Filter, 'Classifier')}
            <div className="space-y-4">
              <div className="space-y-1.5">
                <p className="text-[10px] font-black text-base-content/50 uppercase tracking-widest">Classifier Prompt</p>
                {textBlock(agent.classifierPrompt, 'No classifier prompt configured.')}
              </div>
              {agent.classifierResponseFormat !== null && (
                <div className="space-y-1.5">
                  <p className="text-[10px] font-black text-base-content/50 uppercase tracking-widest">Response Format</p>
                  <pre className="p-4 rounded-xl bg-base-200/50 border border-base-content/5 text-xs font-mono text-base-content/80 overflow-x-auto whitespace-pre-wrap break-all">
                    {classifierFormat}
                  </pre>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const title = agent?.name || location.state?.agent?.name || 'AI Agent';

  return (
    <div className="space-y-6 p-1 sm:p-2 w-full pb-12">
      {/* Breadcrumb */}
      <div>
        <button
          type="button"
          onClick={backToList}
          className="inline-flex items-center gap-2 text-xs font-black uppercase tracking-widest text-base-content/50 hover:text-primary transition-colors group"
        >
          <ArrowLeft size={14} className="group-hover:-translate-x-1 transition-transform" />
          Back to AI Agents
        </button>
      </div>

      {/* Hero Header */}
      <div className="glass-card rounded-2xl p-5 sm:p-6 shadow-sm border border-base-content/10">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="space-y-1 min-w-0">
            <span className="text-[10px] font-black uppercase tracking-widest text-base-content/40">
              AI Agent
            </span>
            <h1 className="text-2xl sm:text-3xl font-black text-base-content tracking-tight break-words">
              {title}
            </h1>
            {agent?.role && <p className="text-xs text-base-content/50">{agent.role}</p>}
          </div>
          {agent && !loading && !serviceError && (
            <div className="flex items-center gap-2 flex-wrap">
              {agent.type && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-base-200 text-base-content/70 border border-base-content/5">
                  {getAiAgentTypeLabel(agent.type)}
                </span>
              )}
              <StatusBadge status={agent.isLive ? 'live' : 'not_live'} />
              {/* A metadata classifier isn't a chat agent, so it has no chat preview. */}
              {!isMetadataClassifierType(agent.type) && (
                <button
                  type="button"
                  onClick={() => setPreviewOpen(true)}
                  className="btn btn-sm btn-ghost border border-base-content/10 rounded-xl gap-2 font-bold"
                >
                  <MessagesSquare size={15} />
                  <span>Preview</span>
                </button>
              )}
              {canUpdateAgent && (
                <button
                  type="button"
                  onClick={() => navigate(`${LIST_ROUTE}/${encodeURIComponent(agentId)}/edit${accountQuery}`, {
                    state: { agent: { id: agent.id, name: agent.name } },
                  })}
                  className="btn btn-sm btn-primary rounded-xl gap-2 font-bold shadow-sm hover:shadow transition-all"
                >
                  <Pencil size={15} />
                  <span>Edit</span>
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {renderContent()}

      {/* Chat preview */}
      {agent && !isMetadataClassifierType(agent.type) && (
        <AgentChatPreview
          isOpen={previewOpen}
          onClose={() => setPreviewOpen(false)}
          agent={agent}
          accountParams={accountParams}
        />
      )}
    </div>
  );
};

export default AiAgentDetail;
